import { API_BASE_URL } from './Constants';

/**
 * Fetches all teams associated with the logged-in player
 * @returns {Promise<Object>} - Object containing team data
 */
export const fetchPlayerTeams = async () => {
  try {
    const authToken = localStorage.getItem("access_token");
    if (!authToken) {
      throw new Error("No authentication token found");
    }

    const response = await fetch(`${API_BASE_URL}/playerapi/my-teams/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch teams: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching player teams:", error);
    throw error;
  }
};

/**
 * Creates a new team
 * @param {Object} teamData - Data for the new team
 * @returns {Promise<Object>} - The created team data
 */
export const createTeam = async (teamData) => {
  try {
    const authToken = localStorage.getItem("access_token");
    if (!authToken) {
      throw new Error("No authentication token found");
    }

    const response = await fetch(`${API_BASE_URL}/teamsapi/create/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(teamData)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create team: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error creating team:", error);
    throw error;
  }
};

/**
 * Gets detailed information about a specific team
 * @param {string} teamId - The ID of the team
 * @returns {Promise<Object>} - Detailed team data
 */
export const getTeamDetails = async (teamId) => {
  try {
    const authToken = localStorage.getItem("access_token");
    if (!authToken) {
      throw new Error("No authentication token found");
    }

    const response = await fetch(`${API_BASE_URL}/teamsapi/${teamId}/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch team details: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error fetching team ${teamId} details:`, error);
    throw error;
  }
};

/**
 * Fetches all members of a specific team
 * @param {string} teamId - The ID of the team
 * @returns {Promise<Array>} - Array of team members
 */
export const getTeamMembers = async (teamId) => {
  try {
    const authToken = localStorage.getItem("access_token");
    if (!authToken) {
      throw new Error("No authentication token found");
    }

    const response = await fetch(`${API_BASE_URL}/teamsapi/${teamId}/members/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch team members: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error fetching team ${teamId} members:`, error);
    throw error;
  }
};

/**
 * Removes a member from a team (only team owner can perform this action)
 * @param {string} teamId - The ID of the team
 * @param {string} playerId - The ID of the player to remove
 * @returns {Promise<Object>} - The response data
 */
export const removeTeamMember = async (teamId, playerId) => {
  try {
    const authToken = localStorage.getItem("access_token");
    if (!authToken) {
      throw new Error("No authentication token found");
    }

    const response = await fetch(`${API_BASE_URL}/teamsapi/${teamId}/remove-member/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ player_id: playerId })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to remove team member: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error removing player from team:`, error);
    throw error;
  }
};

/**
 * Fetches all pending invitations for the current player
 * @returns {Promise<Object>} - Object containing invitation data
 */
export const fetchPlayerInvitations = async () => {
  try {
    const authToken = localStorage.getItem("access_token");
    if (!authToken) {
      throw new Error("No authentication token found");
    }

    const response = await fetch(`${API_BASE_URL}/playerapi/invitations/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch invitations: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching player invitations:", error);
    throw error;
  }
};

/**
 * Responds to a team invitation (accept or reject)
 * @param {string} teamId - The ID of the team that sent the invitation
 * @param {string} response - The response ('accept' or 'reject')
 * @returns {Promise<Object>} - Response data
 */
export const respondToInvitation = async (teamId, response) => {
  try {
    const authToken = localStorage.getItem("access_token");
    if (!authToken) {
      throw new Error("No authentication token found");
    }

    const apiResponse = await fetch(`${API_BASE_URL}/playerapi/respond-invitation/${teamId}/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ response })
    });

    if (!apiResponse.ok) {
      const errorText = await apiResponse.text();
      throw new Error(`Failed to respond to invitation: ${apiResponse.status} ${apiResponse.statusText} - ${errorText}`);
    }

    const data = await apiResponse.json();
    return data;
  } catch (error) {
    console.error(`Error responding to team ${teamId} invitation:`, error);
    throw error;
  }
};

/**
 * Searches for players by username
 * @param {string} searchTerm - Search term (minimum 3 characters)
 * @returns {Promise<Array>} - Search results containing user IDs and emails
 */
export const searchPlayers = async (searchTerm) => {
  try {
    // Validate search term
    if (!searchTerm || searchTerm.length < 3) {
      throw new Error("Search term must be at least 3 characters long");
    }

    const authToken = localStorage.getItem("access_token");
    if (!authToken) {
      throw new Error("No authentication token found");
    }

    const response = await fetch(`${API_BASE_URL}/playerapi/search/?username=${encodeURIComponent(searchTerm)}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to search players: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    
    // Extract results array from the response
    let resultsArray = [];
    if (data.results && Array.isArray(data.results)) {
      // Format matches: { "search_term": "...", "count": X, "results": [...] }
      resultsArray = data.results;
    } else if (Array.isArray(data)) {
      // Format matches direct array: [...]
      resultsArray = data;
    } else {
      console.warn("Unexpected search result format:", data);
      resultsArray = [];
    }
    
    return resultsArray;
  } catch (error) {
    console.error(`Error searching players:`, error);
    throw error;
  }
};

/**
 * Invites a player to join a team
 * @param {string} teamId - The ID of the team
 * @param {string} playerId - The ID of the player to invite
 * @returns {Promise<Object>} - The response data
 */
export const invitePlayer = async (teamId, playerId) => {
  try {
    const authToken = localStorage.getItem("access_token");
    if (!authToken) {
      throw new Error("No authentication token found");
    }

    const response = await fetch(`${API_BASE_URL}/teamsapi/${teamId}/invite-player/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ player_id: playerId })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to invite player: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error inviting player to team:`, error);
    throw error;
  }
};

/**
 * Deletes a team (only team owner can perform this action)
 * @param {string} teamId - The ID of the team
 * @returns {Promise<Object>} - The response data
 */
export const deleteTeam = async (teamId) => {
  try {
    const authToken = localStorage.getItem("access_token");
    if (!authToken) {
      throw new Error("No authentication token found");
    }

    const response = await fetch(`${API_BASE_URL}/teamsapi/${teamId}/?confirm=true`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to delete team: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error deleting team ${teamId}:`, error);
    throw error;
  }
};
